import { useState, useEffect } from 'react';
import { Dialog } from '@printtat/ui';
import { X } from 'lucide-react';
import { Button, IconButton, TextField, TextFieldSlot, Select, Label } from '@printtat/ui';
import type { DiscountCode } from '@printtat/shared';

const TYPE_OPTIONS = [
  { value: 'percentage', label: 'Percentage' },
  { value: 'fixed_amount', label: 'Fixed Amount (EGP)' },
  { value: 'free_shipping', label: 'Free Shipping' },
];

const ACTIVE_OPTIONS = [
  { value: 'true', label: 'Active' },
  { value: 'false', label: 'Inactive' },
];

const PriceInput = ({ value, onChange, placeholder, disabled }: {
  value: string;
  onChange: (val: string) => void;
  placeholder?: string;
  disabled?: boolean;
}) => (
  <TextField
    type="number"
    min="0"
    step="0.01"
    value={value}
    onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
    placeholder={placeholder}
    disabled={disabled}
  >
    <TextFieldSlot side="right">
      <span className="text-xs font-medium text-gray-400">EGP</span>
    </TextFieldSlot>
  </TextField>
);

const fieldLabelClass = 'block text-gray-700 mb-1';

interface DiscountCodeModalProps {
  discount: DiscountCode | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (data: Record<string, unknown>) => void;
  loading?: boolean;
}

export const DiscountCodeModal = ({ discount, open, onOpenChange, onSave, loading }: DiscountCodeModalProps) => {
  const isEdit = !!discount;

  const [code, setCode] = useState('');
  const [type, setType] = useState('percentage');
  const [value, setValue] = useState('');
  const [maxDiscountAmount, setMaxDiscountAmount] = useState('');
  const [minOrderAmount, setMinOrderAmount] = useState('');
  const [maxUses, setMaxUses] = useState('');
  const [maxUsesPerUser, setMaxUsesPerUser] = useState('');
  const [startsAt, setStartsAt] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [description, setDescription] = useState('');
  const [isActive, setIsActive] = useState('true');

  useEffect(() => {
    if (discount) {
      setCode(discount.code);
      setType(discount.type);
      setValue(String(discount.value));
      setMaxDiscountAmount(discount.maxDiscountAmount != null ? String(discount.maxDiscountAmount) : '');
      setMinOrderAmount(discount.minOrderAmount != null ? String(discount.minOrderAmount) : '');
      setMaxUses(discount.maxUses != null ? String(discount.maxUses) : '');
      setMaxUsesPerUser(discount.maxUsesPerUser != null ? String(discount.maxUsesPerUser) : '');
      setStartsAt(discount.startsAt ? discount.startsAt.slice(0, 16) : '');
      setExpiresAt(discount.expiresAt ? discount.expiresAt.slice(0, 16) : '');
      setDescription(discount.description ?? '');
      setIsActive(String(discount.isActive));
    } else {
      setCode('');
      setType('percentage');
      setValue('');
      setMaxDiscountAmount('');
      setMinOrderAmount('');
      setMaxUses('');
      setMaxUsesPerUser('');
      setStartsAt('');
      setExpiresAt('');
      setDescription('');
      setIsActive('true');
    }
  }, [discount, open]);

  const handleSave = () => {
    const data: Record<string, unknown> = {
      type,
      value: Number(value),
      isActive: isActive === 'true',
    };

    if (!isEdit) {
      data.code = code.toUpperCase();
    }

    if (maxDiscountAmount) data.maxDiscountAmount = Number(maxDiscountAmount);
    else if (isEdit) data.maxDiscountAmount = null;

    if (minOrderAmount) data.minOrderAmount = Number(minOrderAmount);
    else if (isEdit) data.minOrderAmount = null;

    if (maxUses) data.maxUses = Number(maxUses);
    else if (isEdit) data.maxUses = null;

    if (maxUsesPerUser) data.maxUsesPerUser = Number(maxUsesPerUser);
    else if (isEdit) data.maxUsesPerUser = null;

    if (startsAt) data.startsAt = new Date(startsAt).toISOString();
    else if (isEdit) data.startsAt = null;

    if (expiresAt) data.expiresAt = new Date(expiresAt).toISOString();
    else if (isEdit) data.expiresAt = null;

    if (description) data.description = description;
    else if (isEdit) data.description = null;

    onSave(data);
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 z-50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <Dialog.Content className="fixed left-[50%] top-[50%] z-50 w-full max-w-md translate-x-[-50%] translate-y-[-50%] border bg-white p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 rounded-xl max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-5">
            <Dialog.Title className="text-lg font-semibold text-gray-900">
              {isEdit ? 'Edit discount code' : 'Create discount code'}
            </Dialog.Title>
            <Dialog.Close asChild>
              <IconButton variant="ghost" size="sm" aria-label="Close">
                <X size={18} />
              </IconButton>
            </Dialog.Close>
          </div>

          <div className="space-y-4">
            {/* Code */}
            <div>
              <Label className={fieldLabelClass}>Code</Label>
              <TextField
                className="font-mono uppercase tracking-wider"
                value={code}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCode(e.target.value.toUpperCase())}
                placeholder="e.g. SUMMER20"
                disabled={isEdit}
              />
              {isEdit && <p className="text-xs text-gray-400 mt-1">Code cannot be changed after creation</p>}
            </div>

            {/* Type + Value (+ Max discount cap when percentage) */}
            <div className={`grid gap-3 ${type === 'percentage' ? 'grid-cols-3' : 'grid-cols-2'}`}>
              <div>
                <Label className={fieldLabelClass}>Type</Label>
                <Select
                  options={TYPE_OPTIONS}
                  value={type}
                  onChange={(e) => setType(e.target.value)}
                />
              </div>
              <div>
                <Label className={fieldLabelClass}>Value</Label>
                {type === 'fixed_amount' ? (
                  <PriceInput
                    value={value}
                    onChange={setValue}
                    placeholder="0"
                  />
                ) : (
                  <TextField
                    type="number"
                    min="0"
                    value={value}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setValue(e.target.value)}
                    placeholder={type === 'percentage' ? '0–100' : '—'}
                    disabled={type === 'free_shipping'}
                  >
                    {type === 'percentage' && (
                      <TextFieldSlot side="right">
                        <span className="text-xs font-medium text-gray-400">%</span>
                      </TextFieldSlot>
                    )}
                  </TextField>
                )}
              </div>
              {type === 'percentage' && (
                <div>
                  <Label className={fieldLabelClass}>Max discount cap</Label>
                  <PriceInput
                    value={maxDiscountAmount}
                    onChange={setMaxDiscountAmount}
                    placeholder="No cap"
                  />
                </div>
              )}
            </div>

            {/* Min order + limits row */}
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className={fieldLabelClass}>Min order</Label>
                <PriceInput
                  value={minOrderAmount}
                  onChange={setMinOrderAmount}
                  placeholder="None"
                />
              </div>
              <div>
                <Label className={fieldLabelClass}>Max uses</Label>
                <TextField
                  type="number"
                  value={maxUses}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setMaxUses(e.target.value)}
                  placeholder="&#8734;"
                />
              </div>
              <div>
                <Label className={fieldLabelClass}>Per customer</Label>
                <TextField
                  type="number"
                  value={maxUsesPerUser}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setMaxUsesPerUser(e.target.value)}
                  placeholder="&#8734;"
                />
              </div>
            </div>

            {/* Date range */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className={fieldLabelClass}>Starts at</Label>
                <TextField
                  type="datetime-local"
                  value={startsAt}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setStartsAt(e.target.value)}
                />
              </div>
              <div>
                <Label className={fieldLabelClass}>Expires at</Label>
                <TextField
                  type="datetime-local"
                  value={expiresAt}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setExpiresAt(e.target.value)}
                />
              </div>
            </div>

            {/* Status + Description */}
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className={fieldLabelClass}>Status</Label>
                <Select
                  options={ACTIVE_OPTIONS}
                  value={isActive}
                  onChange={(e) => setIsActive(e.target.value)}
                />
              </div>
              <div className="col-span-2">
                <Label className={fieldLabelClass}>Note</Label>
                <TextField
                  value={description}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDescription(e.target.value)}
                  placeholder="Internal note (admin only)"
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <Dialog.Close asChild>
              <Button variant="outline" disabled={loading}>Cancel</Button>
            </Dialog.Close>
            <Button
              onClick={handleSave}
              loading={loading}
              disabled={!code || (!value && type !== 'free_shipping')}
            >
              {isEdit ? 'Save changes' : 'Create code'}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
